import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import EditorPage from './pages/EditorPage';
import MonitoringPage from './pages/MonitoringPage';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import RequireAuth from './components/common/RequireAuth';
import UpgradePrompt from './components/common/UpgradePrompt';

const UploadPage = lazy(() => import('./pages/UploadPage'));
const IVReportingPage = lazy(() => import('./pages/IVReportingPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const InvitationAcceptPage = lazy(() => import('./pages/InvitationAcceptPage'));
const OnboardingWizard = lazy(() => import('./pages/OnboardingWizard'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const SharePage = lazy(() => import('./pages/SharePage'));
const ShareViewerPage = lazy(() => import('./pages/ShareViewerPage'));

const AuthFallback = <div style={{ padding: '2rem', textAlign: 'center' }}>Loading…</div>;

/**
 * SoleadLab 2.0 - Main Application Component
 *
 * Routes:
 * - /editor                - Main inspection editor (requires auth)
 * - /monitoring            - Monitoring dashboard (requires auth)
 * - /iv                    - IV Reporting (requires auth)
 * - /onboarding            - First-time org/team/site setup (requires auth)
 * - /login                 - Sign in
 * - /register              - Self-service signup
 * - /verify-email/:token   - Email verification landing
 * - /reset-password/:token - Password setup/reset landing (M-02F)
 * - /invite/:token         - Invitation accept landing
 * - /upload/:token         - Mobile upload portal (public, token-based)
 * - /share/:token          - View-only share-link landing (public, token-based) — F-02D
 * - /share/:token/viewer   - Read-only project viewer (public, token-based) — F-02E-2A
 * - /                      - Redirects to /editor
 */
function App() {
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Routes>
        {/* Public auth routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<Suspense fallback={AuthFallback}><RegisterPage /></Suspense>} />
        <Route path="/verify-email/:token" element={<Suspense fallback={AuthFallback}><VerifyEmailPage /></Suspense>} />
        <Route path="/reset-password/:token" element={<Suspense fallback={AuthFallback}><ResetPasswordPage /></Suspense>} />
        <Route path="/invite/:token" element={<Suspense fallback={AuthFallback}><InvitationAcceptPage /></Suspense>} />
        <Route path="/upload/:token" element={<Suspense fallback={AuthFallback}><UploadPage /></Suspense>} />
        {/* F-02E-2A: read-only project viewer — public, no RequireAuth.
            Declared BEFORE the handshake route so react-router's longest-
            match prefers the more-specific `/share/:token/viewer` path. */}
        <Route path="/share/:token/viewer" element={<Suspense fallback={AuthFallback}><ShareViewerPage /></Suspense>} />
        {/* F-02D: view-only share-link landing — public, no RequireAuth */}
        <Route path="/share/:token" element={<Suspense fallback={AuthFallback}><SharePage /></Suspense>} />

        {/* Protected routes */}
        <Route path="/dashboard" element={<RequireAuth><DashboardPage /></RequireAuth>} />
        <Route path="/editor" element={<RequireAuth><EditorPage /></RequireAuth>} />
        <Route path="/monitoring" element={<RequireAuth><MonitoringPage /></RequireAuth>} />
        <Route path="/iv" element={<RequireAuth><Suspense fallback={AuthFallback}><IVReportingPage /></Suspense></RequireAuth>} />
        <Route path="/onboarding" element={<RequireAuth><Suspense fallback={AuthFallback}><OnboardingWizard /></Suspense></RequireAuth>} />
        <Route path="/admin" element={<RequireAuth><Suspense fallback={AuthFallback}><AdminPage /></Suspense></RequireAuth>} />

        {/* Default redirect — dashboard is the new landing for authenticated users. */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />

        {/* Placeholder routes */}
        <Route path="/viewer" element={<ComingSoon page="Operator Viewer" />} />
        <Route path="/client" element={<ComingSoon page="Client Viewer" />} />

        {/* 404 fallback */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>

      {/* Phase 3 SaaS — global plan-limit modal. Renders only when apiFetch
          intercepts a 403/PLAN_LIMIT response and pushes the notice into
          billingStore. */}
      <UpgradePrompt />
    </BrowserRouter>
  );
}

/**
 * Placeholder component for routes not yet implemented
 */
function ComingSoon({ page }: { page: string }) {
  return (
    <div className="coming-soon">
      <h1>{page}</h1>
      <p>This page will be implemented in a future phase.</p>
      <a href="/editor">Go to Editor</a>
    </div>
  );
}

export default App;
